import { useEffect, useRef, useState } from "react";
import { accountScope, type AccountScope } from "@heraion/shared";

import type { ResourceCache } from "./Logger/state/asyncListCache";

/** #397: `fresh` once it is there, else the copy `cache` kept from the last
 *  time. Read once at mount; each new `fresh` is written through. For a value
 *  a panel derives (small) from a read too large to keep. No cache (a coach's
 *  view of an athlete): `fresh` only. Past a sign-out nothing goes back: each
 *  `fresh` is written under the scope taken in the render that computed it
 *  (#398), never one held by the store, which a host may rebuild. */
export function usePersistedCopy<T>(
  cache: ResourceCache<T> | undefined,
  fresh: T | null,
): T | null {
  // The copy travels with the scope it was read under (#423): once the
  // account moves it is the previous account's and is no longer shown.
  const [kept, setKept] = useState<{ value: T; scope: AccountScope } | null>(null);
  // A host may build the cache per render: the ref keeps the effects still.
  const cacheRef = useRef(cache);
  cacheRef.current = cache;
  useEffect(() => {
    let cancelled = false;
    const scope = accountScope(); // #423: the account this read is for
    void cacheRef.current?.read().then((value) => {
      if (!cancelled && scope.current() && value !== null) setKept({ value, scope });
    });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    // The account `fresh` was computed for: the commit where it changed.
    if (fresh !== null) {
      void cacheRef.current?.write(fresh, accountScope());
    }
  }, [fresh]);
  return fresh ?? (kept && kept.scope.current() ? kept.value : null);
}
